import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { todayIST } from "@/lib/dates";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

export default function Reports() {
  const [ranks, setRanks] = useState([]);
  const [today, setToday] = useState([]);

  useEffect(() => {
    api.get("/dashboard/ranking").then((r) => setRanks(r.data));
    const d = todayIST();
    api.get(`/attendance/all?date=${d}`).then((r) => setToday(r.data));
  }, []);

  const byShift = ["OFFICE","COLLECTION_A","COLLECTION_B","COLLECTION_C"].map((s) => ({
    shift: s.replace("_"," "),
    present: today.filter((t) => t.shift === s && t.in_at).length,
    late: today.filter((t) => t.shift === s && t.computed?.late).length,
  }));

  const scoreBuckets = [
    { range: "90-100", count: ranks.filter((r) => r.final_score >= 90).length, color: "#22C55E" },
    { range: "70-89",  count: ranks.filter((r) => r.final_score >= 70 && r.final_score < 90).length, color: "#E50914" },
    { range: "50-69",  count: ranks.filter((r) => r.final_score >= 50 && r.final_score < 70).length, color: "#F59E0B" },
    { range: "<50",    count: ranks.filter((r) => r.final_score < 50).length, color: "#FF3B30" },
  ];

  return (
    <div className="p-6 md:p-10">
      <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Insights</p>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">Reports & Analytics</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Today · Shift Coverage">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byShift}>
              <CartesianGrid stroke="#2D2D35" vertical={false} />
              <XAxis dataKey="shift" stroke="#A1A1AA" fontSize={11} />
              <YAxis stroke="#A1A1AA" fontSize={11} />
              <Tooltip contentStyle={{ background: "#16161C", border: "1px solid #2D2D35", color: "#fff" }} />
              <Bar dataKey="present" name="Present" fill="#22C55E" radius={[4,4,0,0]} />
              <Bar dataKey="late" name="Late" fill="#F59E0B" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Employee Score Distribution">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={scoreBuckets}>
              <CartesianGrid stroke="#2D2D35" vertical={false} />
              <XAxis dataKey="range" stroke="#A1A1AA" fontSize={11} />
              <YAxis stroke="#A1A1AA" fontSize={11} />
              <Tooltip contentStyle={{ background: "#16161C", border: "1px solid #2D2D35", color: "#fff" }} />
              <Bar dataKey="count" radius={[4,4,0,0]}>
                {scoreBuckets.map((b, i) => <Cell key={i} fill={b.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Top Performers" className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ranks.slice(0, 6).map((r, i) => (
              <div key={r.id} className="bg-[#0D0D10] border border-[#2D2D35] rounded-md p-4 flex items-center gap-3">
                <div className="font-mono text-2xl text-[#E50914] w-8">#{i+1}</div>
                <div className="flex-1">
                  <div className="text-white text-sm">{r.name}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{r.employee_id} · {r.shift}</div>
                </div>
                <div className="font-mono text-xl">{r.final_score}</div>
              </div>
            ))}
            {!ranks.length && <div className="text-sm text-zinc-500 p-4">No data yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-[#16161C] border border-[#2D2D35] rounded-md ${className}`}>
      <div className="px-5 py-4 border-b border-[#2D2D35] text-sm font-medium tracking-wide">{title}</div>
      <div className="p-4">{children}</div>
    </div>
  );
}
